import { useFetchHomeGroups } from "src/data/homeGroups/queries/homeGroupQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomes } from "src/data/organizations/queries/homeQueries"
import {
  useFetchTaskCategories,
  useFetchTasks,
} from "src/data/taskManagement/taskManagementQueries"
import {
  TTask,
  TTaskFetchFilters,
} from "src/data/taskManagement/taskManagementTypes"
import { typedFromEntries, unique } from "src/utils/genericUtil"

export function useTaskTypeLabels(): Record<TTask["category"], string> {
  const { orgId } = useOrganization()

  const fetchTaskCategories = useFetchTaskCategories({ orgId })

  const taskTypes = fetchTaskCategories.data?.categories || []

  const taskTypeEntries = typedFromEntries(
    taskTypes.map(({ value, label }) => [value, label] as const)
  )

  return taskTypeEntries
}

export function useFetchTaskTableData(filters: TTaskFetchFilters) {
  const { orgId } = useOrganization()

  const fetchTasks = useFetchTasks({
    orgId,
    filters,
  })

  const tasks = fetchTasks.data?.tasks ?? []

  const homeIds = tasks
    .map((task) => task.home_id)
    .filter((id): id is string => typeof id === "string")

  const fetchHomes = useFetchHomes({
    orgId,
    filters: { ids: homeIds },
  })

  const homeOptions = fetchHomes.data?.homes ?? []

  const uniqueHomeGroupIds = unique(homeOptions.map((t) => t.homegroup_id))
    .filter(Boolean)
    .join(",")

  const fetchHomeGroups = useFetchHomeGroups({
    orgId,
    filter: { ids: uniqueHomeGroupIds },
    options: {
      // The backend expects a comma separated string of ids each with a minimum length of 24
      enabled: uniqueHomeGroupIds.length >= 24,
    },
  })

  const homeGroupOptions = fetchHomeGroups.data?.homegroups ?? []

  const loading =
    fetchTasks.isLoading ||
    fetchHomes.isLoading ||
    fetchHomeGroups.isInitialLoading

  const error =
    fetchTasks.isError || fetchHomes.isError || fetchHomeGroups.isError

  return {
    loading,
    tasks,
    homes: homeOptions,
    homeGroups: homeGroupOptions,
    pagingTotalCount: fetchTasks.data?.paging.total_count ?? 0,
    error,
  }
}
